<template>
  <b-row>
    <b-col cols="12" md="10" offset-md="1">
      <b-card class="shadow mx-md-10 px-1 px-md-10 py-2 mb-5">

        <onboarding-stepper
          v-if="currentStep.stepper"
          :currentStep="currentStep"
          :steps="steps"
        />

        <b-row class="pt-5 pb-4 px-sm-5">
          <b-col lg="4" class="px-4">
            <b-jumbotron>
              <p v-if="templates.title"><b>{{ templates.title }}</b></p>
              <p v-else><b>Skills & Qualifications</b></p>
              <p v-if="templates.description">{{ templates.description }}</p>
              <p v-else>A deeper dive into your skills, interests, qualifications and more to build your profile.</p>
            </b-jumbotron>
            <div class="py-3 px-3">
              <b-img
                v-if="company && company.branding && company.branding.onboardingSkillsImage && company.branding.onboardingSkillsImage.fileLocation"
                :src="company.branding.onboardingSkillsImage.fileLocation"
                fluid
              ></b-img>
            </div>
          </b-col>
          <b-col lg="8" class="px-4">
            <!-- Current skills related fields -->
            <h3 v-if="$root.isCXR" class="mb-4">Your Profile</h3>
            <h3 v-else class="mb-4">Skills and qualifications</h3>
            <div
              v-for="({ key, name, description, type, options, departments, required }) in customFields"
              :key="key"
              class="mb-5"
            >
              <b-form-group
                v-if="['STRING', 'ENUM', 'LOCATION', 'TEXTAREA', 'CV_UPLOAD'].includes(type) && (!departments || departments.filter(id => departmentIds.includes(id)).length)"
                :label-for="`input-${key}`"
              >
                <h4><b>{{name}}{{required ? '*' : ''}}</b></h4>
                <p class="text-muted">{{description}}</p>
                <b-form-select v-if="type === 'ENUM' && options" :id="`input-${key}`" type="text" :required="required"
                  :options="options" :value="inputValues[key]" @input="value => inputValues[key] = value">
                    <template v-slot:first><option :value="null">-- Please select an option --</option></template>
                </b-form-select>
                <b-form-input
                  v-else-if="type === 'RANGE'"
                  id="`input-${key}`"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  type="range"
                  min="0"
                  max="200000"
                ></b-form-input>
                <location-search
                  v-else-if="type === 'LOCATION'"
                  :value="inputValues[key] ? inputValues[key].location : null"
                  :multiple="true"
                  @input="value => $set(inputValues, key, {
                    remote: inputValues[key] ? inputValues[key].remote : null,
                    location: value,
                  })"
                />
                <b-form-textarea
                  v-else-if="type === 'TEXTAREA'"
                  :id="`input-${key}`"
                  type="text"
                  :required="required"
                  :value="inputValues[key]"
                  @input="value => $set(inputValues, key, value)"
                  rows="3"
                  max-rows="6"
                ></b-form-textarea>
                <div v-else-if="type === 'CV_UPLOAD'">
                  <asset-block class="m-0" accept=".txt,.rtf,.pdf,.doc,.docx,.odt" :field="{ type: 'DOCUMENT' }"
                    id="formResume" placeholder="Select a resume" v-model="inputValues[key]"/>
                </div>
                <b-form-input v-else :id="`input-${key}`" type="text" :required="required"
                  :value="inputValues[key]" @input="value => inputValues[key] = value"/>
              </b-form-group>
              <job-title-selector
                v-if="type === 'JOB_TITLES'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :invalidFeedback="invalidFeedback(required, inputValues[key])"
                @setJobTitleID="value => jobTitleID = value"
                @input="value => inputValues[key] = value"
                @setJobCategory="setJobCategory($event)"
              />
              <job-skills-selector
                v-if="type === 'JOB_SKILLS'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :jobTitleID="jobTitleID"
                :isFreeText="!$root.isCXR"
                @input="skillsInput($event, key, 'SKILL')"
              />
              <job-business-area-selector
                v-if="type === 'JOB_BUSINESS_AREA'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <multi-selector
                v-if="type === 'MULTI_SELECT'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <languages-selector
                v-if="type === 'LANGUAGES'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <qualifications-editor
                v-if="type === 'QUALIFICATIONS'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <date-editor
                v-if="type === 'DATE'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <job-history-editor
                v-if="type === 'JOB_HISTORY'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />

            </div>

            <!-- Development goals related questions -->
            <!-- template v-if="Array.isArray(customDevelopmentFields) && customDevelopmentFields.length">
              <hr/>
              <h3 class="mb-4">Development skills</h3>
              <div
                v-for="({ key, name, description, type, options, required }) in customDevelopmentFields"
                :key="key"
                class="mb-5"
              >
                <job-skills-selector
                  v-if="type === 'JOB_DEVELOPMENT_SKILLS'"
                  :value="inputValues[key]"
                  :name="name"
                  :description="description"
                  :isFreeText="!$root.isCXR"
                  :options="options"
                  :required="required"
                  @input="skillsInput($event, key, 'DEVELOPMENT')"
                />
              </div>
            </template -->
          </b-col>
        </b-row>

        <b-row class="px-5">
          <b-col class="d-flex justify-content-between align-items-center px-4">
            <a class="text-secondary" href="#" @click.prevent="() => $emit('goBack')">Go back</a>
            <b-button @click.prevent="submitForm" class="px-5" variant="primary">Continue</b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { mapRoot } from '@src/mixins/root';
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import JobTitleSelector from '@/components/profile/JobTitleSelector';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import JobBusinessAreaSelector from '@/components/profile/JobBusinessAreaSelector';
import LocationSearch from '@/components/misc/LocationSearch';
import LanguagesSelector from '@/components/profile/LanguagesSelector';
import MultiSelector from '@/components/profile/MultiSelector';
import QualificationsEditor from '@/components/profile/QualificationsEditor';
import DateEditor from '@/components/profile/DateEditor';
import JobHistoryEditor from '@/components/profile/JobHistoryEditor';
import RegisterQuestionsQuery from '@/graphql/queries/RegisterQuestions.gql';
import AssetBlock from '@/components/settings/AssetBlock';
import gql from 'graphql-tag';

const templateMap = {
  ONBOARDING_SKILLS_TITLE: 'title',
  ONBOARDING_SKILLS_DESCRIPTION: 'description',
};

const WriteUserQuestionsMutation = gql`
  mutation WriteUserCustomFields($update: [ UpdateUserCustomFieldInput! ]!) {
    updateManyUserCustomFields(update: $update) {
      key
      value
    }
  }
`;

const UpdateUserResumeMutation = gql`
  mutation UpdateUserResume($userID: ID, $resumeID: ID) {
    updateUser(where: { id: $userID }, update: { resume: { id: $resumeID } }) {
      id
    }
  }
`;

const AddTaxonomySkillSuggestionsMutation = gql`
  mutation AddTaxonomySkillsSuggestions($skills: [String!]) {
    createTaxonomySkillSuggestions(skills: $skills)
  }
`;

export default {
  name: 'OnboardingSkills',
  components: {
    OnboardingStepper,
    JobTitleSelector,
    JobSkillsSelector,
    JobBusinessAreaSelector,
    LocationSearch,
    LanguagesSelector,
    QualificationsEditor,
    DateEditor,
    JobHistoryEditor,
    AssetBlock,
    MultiSelector
  },
  props: {
    currentStep: {
      type: Object,
      default: () => null,
    },
    steps: {
      type: Array,
      default: () => [],
    }
  },
  data: () => ({
    fatalErr: null,
    err: null,
    inputValues: {},
    canSubmit: true,
    jobTitleID: null,
    skillSuggestions: {
      SKILL: [],
      DEVELOPMENT: []
    },
  }),
  apollo: {
    company: {
      query: RegisterQuestionsQuery,
      update: ({ company }) => company,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { company, user } = data;
          this.$assert(company && company.id, 500, new Error('Failed to load company register questions'));
          this.$assert(user && user.id, 500, new Error('Failed to load user register questions'));
          if (user && Array.isArray(user.customFields) && user.customFields.length) {
            this.inputValues = user.customFields.reduce((values, { key, value }) => {
              try {
                value = JSON.parse(value);
              } catch (e) {
                // not a json
              }
              return Object.assign(values, { [key]: value });
            }, {});
          }
        }
      },
      error(err) {
        this.fatalErr = err;
      }
    },
  },
  computed: {
    ...mapRoot([ 'user' ]),
    templates() {
      const templates = {};
      if (this.company && Array.isArray(this.company.templatedStrings)) {
        const { templatedStrings } = this.company;
        Object.keys(templateMap).forEach(templateKey => {
          const template = templatedStrings.find(el => el.key === templateKey);
          templates[templateMap[templateKey]] = template ? template.value : null;
        });
      }
      console.log(templates);
      return templates;
    },
    departmentIds() {
      const { departments } = this.user;
      return Array.isArray(departments) ? departments.map(({ id }) => id) : [];
    },
    customFields() {
      const { customFields } = this.company || {};
      const fields = (Array.isArray(customFields) ? customFields : []).map(({ options, departments, ...field }) => ({
        ...field,
        options: options ? JSON.parse(options) : null,
        departments: departments ? departments.map(({ id }) => id) : null,
      })).filter(field => field.group === this.currentStep.name && !field.key.startsWith('HIDDEN'));
      console.log(fields);
      return fields;
    },
    customDevelopmentFields() {
      const { customFields } = this.company || {};
      const fields = (Array.isArray(customFields) ? customFields : []).map(({ options, departments, ...field }) => ({
        ...field,
        options: options ? JSON.parse(options) : null,
        departments: departments ? departments.map(({ id }) => id) : null,
      })).filter(field => field.group === this.currentStep.name && [ 'JOB_DEVELOPMENT_SKILLS' ].includes(field.type));
      console.log(fields);
      return fields;
    },
  },
  methods: {
    invalidFeedback(required, val) {
      const { canSubmit } = this;
      if (!required || canSubmit) return null;
      const isInvalid = (Array.isArray(val) && !val.length) || (!Array.isArray(val) && !val);
      return isInvalid ? 'This field is required.' : null;
    },
    async submitForm() {
      try {
        const { inputValues, customFields } = this;

        if (Object.keys(inputValues).length) {
          this.canSubmit = customFields.reduce((out, el) => {
            if (el.required) {
              const val = inputValues[el.key];
              out = out && ((Array.isArray(val) && val.length) || (!Array.isArray(val) && val));
            }
            return out;
          }, true);
          if (!this.canSubmit) {
            setTimeout(() => this.$scrollTo('.text-danger'), 100);
            return;
          }

          await this.$apollo.mutate({
            mutation: WriteUserQuestionsMutation,
            variables: {
              update: Object.keys(inputValues).map(key => ({
                key,
                value: typeof inputValues[key] === 'string' ? inputValues[key] : (inputValues[key] ? JSON.stringify(inputValues[key]) : null),
              })).filter(({ value }) => value),
            },
            update(store, { data: { updateManyUserCustomFields: fields } }) {
              const data = store.readQuery({ query: RegisterQuestionsQuery });
              data.user.customFields = data.user.customFields.map(field => {
                if (inputValues[field.key]) {
                  field.value = inputValues[field.key];
                }
                return field;
              });
              store.writeQuery({ query: RegisterQuestionsQuery, data });
            },
          });

          // update resume field if defined
          const resumeField = customFields.find(el => el && el.type === 'CV_UPLOAD');
          const resumeKey = resumeField ? resumeField.key : null;
          const resume = resumeKey ? inputValues[resumeKey] : null;
          if (resume && resume.id) {
            await this.$apollo.mutate({
              mutation: UpdateUserResumeMutation,
              variables: { userID: this.user.id, resumeID: resume.id },
            });
          }

          if (!this.$root.isCXR) {
            const { skillSuggestions } = this;
            const suggestedSkills = skillSuggestions['SKILL'].concat(
              skillSuggestions['DEVELOPMENT'].filter(item => skillSuggestions['SKILL'].indexOf(item) < 0)
            );
            await this.$apollo.mutate({
              mutation: AddTaxonomySkillSuggestionsMutation,
              variables: { skills: suggestedSkills }
            });
          }

          Promise.resolve(this.$root.captureLog({
            eventName: 'IM_ONBOARDING_SKILLS_SUCCESS',
            user: { self: true },
          }));
        }

        this.$emit('goNext');
      } catch (err) {
        console.error(err);
        this.err = err;
      }
    },
    skillsInput(input, key, type) {
      if (this.$root.isCXR) {
        this.inputValues[key] = input;
      } else {
        this.inputValues[key] = input.input;
        this.skillSuggestions[type] = input.newTerms;
        console.log('NEW SKILL SUGGESTIONS FROM INPUT: ', this.skillSuggestions);
      }
    },
    setJobCategory(input) {
      console.log('EMITTED 1: ', input);
      const { customFields } = this.company || {};
      const field = (Array.isArray(customFields) ? customFields : [])
        .find(field => field.group === this.currentStep.name && field.key.startsWith('HIDDEN') && field.type === 'JOB_BUSINESS_AREA');
      if (input && field && field.key) {
        this.inputValues[field.key] = input;
      };
      console.log('INPUT VALUES: ', this.inputValues);
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/views/onboarding/Skills.vue